import { useState } from "react"
import Button from "@mui/material/Button"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormLabel from "@mui/material/FormLabel"
import Grid from "@mui/material/Grid2"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import MinMaxParam from "../TripRate/ViewModels/MinMaxViewModel"
import PieChartOptionsViewModel from "../TripRate/ViewModels/PieChartOptionsViewModel"

interface IProps {
    minMaxTimes: MinMaxParam
    getOptions: (vm: PieChartOptionsViewModel) => void
    onOptionsChange?: (vm: PieChartOptionsViewModel) => void
}

export default function PieChartOptions({ minMaxTimes, getOptions, onOptionsChange }: IProps) {
    const [direction, setDirection] = useState<"arrivals" | "departures" | "totals">("totals")
    const [period, setPeriod] = useState<"all" | "select" | "peak">("all")
    const [selectedMin, setSelectedMin] = useState<number>(minMaxTimes.min)
    const [selectedMax, setSelectedMax] = useState<number>(minMaxTimes.max)

    const getViewModel = (
        nextDirection = direction,
        nextPeriod = period,
        nextSelectedMin = selectedMin,
        nextSelectedMax = selectedMax
    ): PieChartOptionsViewModel => ({
        direction: nextDirection,
        period: nextPeriod,
        selectedTime: nextPeriod === "select"
            ? Object.assign(new MinMaxParam(), { min: nextSelectedMin, max: nextSelectedMax })
            : null,
    })

    const handleDirectionChange = (value: "arrivals" | "departures" | "totals") => {
        setDirection(value)
        onOptionsChange?.(getViewModel(value))
    }

    const handlePeriodChange = (value: "all" | "select" | "peak") => {
        setPeriod(value)
        onOptionsChange?.(getViewModel(direction, value))
    }

    const handleSelectedMinChange = (value: number) => {
        setSelectedMin(value)
        onOptionsChange?.(getViewModel(direction, period, value, selectedMax))
    }

    const handleSelectedMaxChange = (value: number) => {
        setSelectedMax(value)
        onOptionsChange?.(getViewModel(direction, period, selectedMin, value))
    }

    const submit = () => {
        const vm: PieChartOptionsViewModel = {
            direction,
            period,
            selectedTime: period === "select"
                ? Object.assign(new MinMaxParam(), { min: selectedMin, max: selectedMax })
                : null,
        }
        getOptions(vm)
    }

    const timeIndexToString = (index: number): string | null => {
        if (index < 1 || index > 24) return null
        const formattedHour = index.toString().padStart(2, "0")
        return `${formattedHour}:00`
    }

    return (
        <Grid container spacing={2}>
            <Grid size={4}>
                <FormControl>
                    <FormLabel>Direction</FormLabel>
                    <RadioGroup
                        row
                        value={direction}
                        onChange={(e) => handleDirectionChange(e.target.value as any)}>
                        <FormControlLabel value="arrivals" control={<Radio />} label="Arrivals" />
                        <FormControlLabel value="departures" control={<Radio />} label="Departures" />
                        <FormControlLabel value="totals" control={<Radio />} label="Totals" />
                    </RadioGroup>
                </FormControl>
            </Grid>

            {period === "select" && (
                <Grid size={4}>
                    <FormControl fullWidth>
                        <FormLabel>Start Time</FormLabel>
                        <Select value={selectedMin} onChange={(e) => handleSelectedMinChange(Number(e.target.value))}>
                            {Array.from({ length: 24 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {timeIndexToString(i + 1)}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormLabel>End Time</FormLabel>
                        <Select value={selectedMax} onChange={(e) => handleSelectedMaxChange(Number(e.target.value))}>
                            {Array.from({ length: 24 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {timeIndexToString(i + 1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            )}

            <Grid size={4}>
                <FormControl>
                    <FormLabel>Period Selection</FormLabel>
                    <RadioGroup
                        row
                        value={period}
                        onChange={(e) => handlePeriodChange(e.target.value as any)}>
                        <FormControlLabel value="all" control={<Radio />} label   ="Use All Times" />
                        <FormControlLabel value="select" control={<Radio />} label="Selected Start/End Time" />
                        <FormControlLabel value="peak" control={<Radio />} label  ="Use Peak Period" />
                    </RadioGroup>
                </FormControl>
            </Grid>

            <Grid size={12}>
                <Button fullWidth={false} variant="contained" onClick={submit}>Apply Filters</Button>
            </Grid>
        </Grid>
    )
}
